/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 10:57:37 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import AsyncAPIListAutoComplete, { AsyncAPIListAutoCompleteProps } from "ui-widgets/select/AsyncAPIListAutoComplete";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import { OSFormInputProps, useControlledInput } from "ui-widgets/forms";
import {
  APIMany2OneSelectAddNewButton,
  APIMany2OneSelectAddNewProps,
} from "ui-widgets/forms/input-fields/related/APIMany2OneSelectAddNewButton";
import { APIMany2OneEditButton } from "ui-widgets/forms/input-fields/related/APIMany2OneEditButton";
import { useAPIFetchItem } from "lib/api";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import {
  APIFormInputErrorWrapper,
  CustomInputValidationErrorWrapper,
  HelperTextInputWrapper,
} from "ui-widgets/forms/FormInputWrappers";

// The props type for the generic APIMany2OneSelectFormInput that has no specific implementation
// The splitting of the two type parameters (i.e. PrimaryObject & RelatedObject) will propagate type inferencing to the calling components.
type GenericAPIMany2OneSelectFormInputProps<PrimaryObject, RelatedObject> = OSFormInputProps<PrimaryObject> &
  AsyncAPIListAutoCompleteProps<RelatedObject> &
  Partial<Pick<APIMany2OneSelectAddNewProps<RelatedObject>, "renderRelatedObjectForm">> & {
    onLoadInitial?: (initialValue: RelatedObject) => void;
    getOptionComponent?: (option: RelatedObject, meta?: { isSelected: boolean }) => React.ReactNode; // If one wants to present the options using a UI component instead of a plain string. For a plain string use getOptionLabel
  };

// the props type for a concrete/specific implementation of APIMany2OneSelectFormInput e.g. a Countries Dropdown field.
export type ConcreteAPIMany2OneSelectFormInputProps<PrimaryObject, RelatedObject> = Omit<
  GenericAPIMany2OneSelectFormInputProps<PrimaryObject, RelatedObject>,
  "apiServicePath" | "itemIdProperty" | "getOptionLabel" | "label"
> &
  Partial<Pick<GenericAPIMany2OneSelectFormInputProps<PrimaryObject, RelatedObject>, "label">>;

export const APIMany2OneSelectFormInput = <PrimaryObject, RelatedObject>(
  props: GenericAPIMany2OneSelectFormInputProps<PrimaryObject, RelatedObject>
) => {
  const {
    controlledProps,
    field: { name, onBlur, onChange, ref, value },
  } = useControlledInput<PrimaryObject>(props);

  const relatedObjectId = value;

  const { apiItem, isMakingAPIRequest: isFetchingAPIItem } = useAPIFetchItem<RelatedObject>({
    apiServicePath: props.apiServicePath,
    apiPathParams: relatedObjectId ? [relatedObjectId] : [],
    apiQueryParams: props.apiQueryParams,
    makeRequestOnInitialMount: Boolean(relatedObjectId),
    dependencyList: [relatedObjectId],
  });

  React.useEffect(() => {
    if (apiItem) props.onLoadInitial?.(apiItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(apiItem)]);

  const {
    defaultValue,
    itemIdProperty,
    inputStartAdornment,
    inputEndAdornment,
    renderRelatedObjectForm,
    ...otherProps
  } = props;

  const resolveDefaultValue = () => {
    if (relatedObjectId === null) return null;

    if (typeof apiItem === "object") return apiItem;

    return props.defaultValue;
  };
  const { handleOpen: handleOpenEdit, handleClose: handleCloseEdit, open: editIsOpen } = useToggleState();

  const showEditForm = editIsOpen && !inputEndAdornment && !!renderRelatedObjectForm;

  return (
    <APIFormInputErrorWrapper schema_field_key={props.schema_field_key} label={props.label}>
      <CustomInputValidationErrorWrapper schema_field_key={props.schema_field_key} label={props.label}>
        <HelperTextInputWrapper helperText={props.helperText}>
          <Stack direction="row" spacing={2}>
            <AsyncAPIListAutoComplete
              fullWidth
              {...otherProps} /** Props below this have higher priority just the way JavaScript destructuring works */
              textFieldProps={{
                ...controlledProps,
                ...otherProps.textFieldProps,
                name,
                ref,
                value,
                onBlur,
              }}
              itemIdProperty={itemIdProperty}
              value={resolveDefaultValue()}
              onChangeCallBack={(obj: RelatedObject) => {
                onChange(obj?.[itemIdProperty as string] ?? null);
                props.onChangeCallBack?.(obj);
              }}
              inputStartAdornment={isFetchingAPIItem ? <CircularProgress size={20} /> : inputStartAdornment}
              inputEndAdornment={
                inputEndAdornment
                  ? inputEndAdornment
                  : renderRelatedObjectForm && (
                      <APIMany2OneEditButton objectId={relatedObjectId} onClick={handleOpenEdit} />
                    )
              }
              renderOption={
                props.getOptionComponent
                  ? (optionProps, option: any) => (
                      <li {...optionProps} key={option[itemIdProperty]}>
                        {props.getOptionComponent?.(option) ?? option[itemIdProperty]}
                      </li>
                    )
                  : null
              }
            />
            <div className="os-many2one-add-new-container">
              <div className="os-many2one-add-button">
                {renderRelatedObjectForm && (
                  <APIMany2OneSelectAddNewButton<PrimaryObject, RelatedObject>
                    onChange={onChange}
                    renderRelatedObjectForm={renderRelatedObjectForm}
                  />
                )}
              </div>
            </div>
            {showEditForm
              ? renderRelatedObjectForm({
                  handleCloseForm: handleCloseEdit,
                  updatePrimaryObject: onChange,
                  formOpenState: editIsOpen,
                  isNew: false,
                })
              : null}
          </Stack>
        </HelperTextInputWrapper>
      </CustomInputValidationErrorWrapper>
    </APIFormInputErrorWrapper>
  );
};
